import {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  useMemo,
  useRef,
  useState,
} from 'react';

import { currencyInputDelimiterFormat } from './currencyInputDelimiterFormat';
import { currencyInputDelimiterGetStepValue } from './currencyInputDelimiterGetStepValue';
import { currencyInputDelimiterSanitize } from './currencyInputDelimiterSanitize';

type Args = {
  allowDecimal: boolean;
  maxDecimalPlaces: number;
  min: number;
  max: number | undefined;
  step: number;
  value: number | undefined;
  onChange: ((changeValue: string) => void) | undefined;
  onBlur: ((event: FocusEvent<HTMLInputElement>) => void) | undefined;
  locale: UserLocale;
};

export function useCurrencyInputDelimiter({
  allowDecimal = false,
  maxDecimalPlaces,
  min,
  max,
  value,
  onChange,
  onBlur,
  step,
  locale,
}: Args) {
  const decimalSeparator = useMemo(
    () => (1.1).toLocaleString(locale).substring(1, 2),
    [locale],
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const [displayValue, rawSetDisplayValue] = useState(
    value || value === 0 ? value.toLocaleString(locale) : '',
  );

  const setDisplayValue = (newValue: string) => {
    // Workaround for Preact
    const input = inputRef.current;
    if (input) input.value = newValue;

    rawSetDisplayValue(newValue);
  };

  const onChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = currencyInputDelimiterSanitize({
      value: event.target.value,
      allowDecimal,
      decimalSeparator,
    });

    const formattedValue = currencyInputDelimiterFormat({
      sanitizedValue,
      decimalSeparator,
      maxDecimalPlaces,
      allowDecimal,
      locale,
    });

    setDisplayValue(formattedValue);
    onChange?.(sanitizedValue);
  };

  const onKeyDownHandler = (event: KeyboardEvent<HTMLInputElement>) => {
    const { key } = event;

    if (key !== 'ArrowUp' && key !== 'ArrowDown') return;

    event.preventDefault(); // prevent default arrow key scrolling behavior

    const newValueNumber = allowDecimal
      ? parseFloat(event.currentTarget.value || '0')
      : parseInt(event.currentTarget.value || '0', 10);

    const newValueWithStep = currencyInputDelimiterGetStepValue({
      value: newValueNumber,
      step: step * (key === 'ArrowUp' ? 1 : -1),
      min,
      max,
    }).toLocaleString(locale);

    setDisplayValue(newValueWithStep);
    onChange?.(newValueWithStep);
  };

  const onBlurHandler = (e: FocusEvent<HTMLInputElement>) => {
    if (e.target.value === decimalSeparator) {
      setDisplayValue('');
    }

    onBlur?.(e);
  };

  return {
    inputRef,
    displayValue,
    onChangeHandler,
    onBlurHandler,
    onKeyDownHandler,
  };
}
